/**
 * What Reach's ads page says before it has a list to show, kept where a render
 * test can reach it — slice 65.
 *
 * **These live apart from `ReachAdsPage` for the reason slice 13 split
 * `ReportCopy.tsx` and slice 17 split `CreditsCells.tsx`.** That page imports
 * the API client, which imports `lib/config.ts`, which reads `import.meta.env`
 * and therefore only evaluates inside Vite — so anything left in it could be
 * rendered by nobody and asserted by nobody. `scripts/verify-surface-screens`
 * renders this file; the list itself is `MetaAdsPanel`, which already lives
 * where that suite renders it.
 *
 * Two states, each a claim the page would otherwise make by omission:
 *
 *   - **No ad account.** {@link NoAdAccount} is the whole page for a
 *     workspace with no Meta row, and the reason no ad route is called for it
 *     (see `ReachAdsPage`). It names the two presses between a draft and
 *     money being spent — publish, which creates the campaign at Meta paused,
 *     and start — because "connect your ad account" reads, to somebody careful
 *     with a bank's budget, like "let Wend spend", and it does not. The link is
 *     the Integrations tab for the account, by `integrationTabPath`, because
 *     Integrations holds every account the workspace connects.
 *   - **No ads yet.** {@link FirstAd} is a connected workspace's first visit.
 *     As a guest panel on the campaigns page the list drew nothing here, so a
 *     workspace that used no ads saw no placeholder on someone else's screen;
 *     on its own page an empty list is the first-run state and must say so. No
 *     button: "New ad" in the top bar is the screen's one primary action, and
 *     a second here would be the same press twice.
 *
 * The rejected alternative was a single "empty" component with a flag. The
 * two states have different readers — one has not connected anything, the
 * other has and is about to compose — and a flag would put both sentences one
 * condition away from being shown to the wrong one.
 */
import { Card, CardContent, buttonVariants } from '@wend/ui';
import { Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';

import { integrationTabPath } from '../integrations/integration-tab-url';

/** No Meta ad account on this workspace: one sentence and the way to connect one. */
export function NoAdAccount() {
  return (
    <Card data-role="reach-ads-no-account">
      <CardContent className="flex flex-col items-start gap-4 p-[18px]">
        <p className="max-w-[60ch] text-[0.92rem] leading-relaxed text-muted">
          Connect your Meta ad account to draft ads here. Nothing reaches Meta until you publish, and a published ad
          stays paused until someone starts it.
        </p>
        <Link to={integrationTabPath('meta-ads')} className={buttonVariants({ size: 'sm' })}>
          Connect your Meta ad account
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * A connected workspace with no ads yet. The sentence is the fact a first-time
 * reader cannot infer: a new ad is a draft in Wend, not a campaign at Meta.
 */
export function FirstAd() {
  return (
    <Card data-role="reach-ads-first">
      <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <span className="grid size-12 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
          <Megaphone aria-hidden="true" className="size-5" />
        </span>
        <div className="flex max-w-[38rem] flex-col gap-2.5">
          <h2 className="text-[1.1rem] font-semibold tracking-[-0.022em]">No Meta ads yet</h2>
          <p className="text-[0.92rem] leading-relaxed text-muted">
            A new ad is saved as a draft in Wend. It reaches Meta only when you publish it.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
